import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Pizza,
  Utensils,
  Beef,
  Drumstick,
  Fish,
  Sandwich,
  ChefHat,
  Sparkles,
  Cake,
  Coffee,
  GlassWater,
  Soup,
  Salad,
  Croissant,
  IceCream,
  Cookie,
  Wine,
  Beer,
  CircleDot,
  Shuffle,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { restaurant_id } from "@/api/lib/url";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchMenuManagementOverview } from "@/redux/slice/menu-manage-slice/menu-slice";

const CATEGORY_ICON_MAP = {
  drinks: GlassWater,
  standard_foods: Utensils,
  pizzas: Pizza,
  burgers: Beef,
  kebabs: Drumstick,
  fish_chips: Fish,
  combos: Sandwich,
  courses: ChefHat,
  special_menu: Sparkles,
  mix_and_match: Shuffle,
  mix_and_matches: Shuffle,
  desserts: Cake,
  tea_coffees: Coffee,
  starters: Soup,
  sides: Salad,
  breakfast: Croissant,
  ice_cream: IceCream,
  snacks: Cookie,
  wine: Wine,
  beer: Beer,
  default: CircleDot,
};

function getCategoryIcon(key) {
  if (!key) return CATEGORY_ICON_MAP.default;
  const normalized = String(key).toLowerCase().trim();
  return CATEGORY_ICON_MAP[normalized] || CATEGORY_ICON_MAP.default;
}


function OverviewSkeleton() {
  return (
    <div className="container mx-auto space-y-6 px-4 pb-8 pt-4">
      {/* Section title placeholder */}
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="h-5 w-44 bg-cblack6" />
        <Skeleton className="h-3 w-64 bg-cblack6" />
      </div>

      {/* Category card grid skeleton */}
      <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 11 }).map((_, idx) => (
          <Skeleton
            key={`cat-skel-${idx}`}
            className="h-[72px] w-full rounded-md bg-cblack6"
          />
        ))}
      </div>

      {/* Pie chart section skeleton */}
      <div className="rounded-md bg-cblack3 p-6">
        <Skeleton className="mb-6 h-5 w-44 bg-cblack6" />
        <div className="flex flex-col items-center justify-center gap-4 md:flex-row md:gap-10">
          <Skeleton className="h-56 w-56 rounded-full bg-cblack6" />
          <div className="flex flex-col gap-3">
            <Skeleton className="h-4 w-44 bg-cblack6" />
            <Skeleton className="h-4 w-44 bg-cblack6" />
          </div>
        </div>
        <div className="mt-6 flex justify-center">
          <Skeleton className="h-5 w-44 bg-cblack6" />
        </div>
      </div>
    </div>
  );
}

function CategoryCard({ label, count, icon: Icon }) {
  return (
    <Card className="h-[72px] rounded-md border border-cblack6 bg-cblack3 shadow-none transition-colors hover:border-cblack7">
      <CardContent className="flex h-full items-center gap-3 p-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-cblack7 text-cyellow">
          <Icon size={24} strokeWidth={1.6} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-[15px] font-medium leading-5 text-cwhite">
            {label}
          </p>
          <p className="text-[15px] leading-5 text-cwhite3">{count}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function MenuStatusPie({ active, inactive }) {
  const total = (active || 0) + (inactive || 0);
  const data = [
    { name: "Active", value: active || 0, color: "#84cc16" }, // green
    { name: "Inactive", value: inactive || 0, color: "#f97316" }, // orange
  ];

  return (
    <div className="rounded-md bg-cblack3 p-6">
      <h3 className="mb-6 text-[15px] font-semibold leading-5 text-cyellow">
        Menu Status
      </h3>
      <div className="flex flex-col items-center justify-center gap-6 md:flex-row md:items-center md:gap-10">
        <div className="relative h-56 w-56 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={95}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {data.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-col gap-4">
          {data.map((entry) => (
            <div
              key={entry.name}
              className="flex items-center gap-3 text-[14px] text-cwhite2"
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="w-32">{entry.name} Menu</span>
              <span className="font-medium text-cwhite3">{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 text-center text-[15px] text-cwhite2">
        Total Menu:{" "}
        <span className="font-semibold text-cwhite">{total}</span>
      </div>
    </div>
  );
}


function MenuOverview() {
  const dispatch = useDispatch();
  const { menuOverview: overview, menuOverviewLoading: loading } = useSelector(
    (state) => state.menuManage.menu
  );

  useEffect(() => {
    dispatch(fetchMenuManagementOverview(restaurant_id));
  }, [dispatch]);

  const categories = overview?.categories || [];
  const menuStatus = overview?.menu_status || {
    active_count: 0,
    inactive_count: 0,
    total_count: 0,
  };

  const categoryItems = useMemo(
    () =>
      categories.map((c) => ({
        key: c.key || c.label,
        label: c.label,
        count: c.count ?? 0,
        icon: getCategoryIcon(c.key),
      })),
    [categories]
  );

  return (
    <div className="min-h-screen w-full bg-color1">
      <Layout2
        LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
        pageName={{ title: "Menu Overview" }}
        paths={[
          { name: "Home", path: "/home" },
          { name: "Back Office", path: "/backoffice/overview" },
          { name: "Menu Management", path: "/backoffice/menu/overview" },
          { name: "Overview" },
        ]}
      >
        {loading && !overview ? (
          <OverviewSkeleton />
        ) : (
          <div className="container mx-auto space-y-6 px-4 pb-8 pt-4 text-cwhite">
            {/* Section title */}
            <div className="flex flex-col items-center text-center">
              <h2 className="text-[16px] font-semibold leading-5 text-cyellow">
                Menu Categories
              </h2>
              <p className="mt-1 text-[12px] text-cwhite3">
                Overview of all menu items grouped by category
              </p>
            </div>

            {/* Category grid */}
            {categoryItems.length > 0 ? (
              <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {categoryItems.map((item) => (
                  <CategoryCard
                    key={item.key}
                    label={item.label}
                    count={item.count}
                    icon={item.icon}
                  />
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-10 text-cwhite3">
                No menu categories found.
              </div>
            )}

            {/* Menu Status Pie Chart */}
            <MenuStatusPie
              active={menuStatus.active_count}
              inactive={menuStatus.inactive_count}
            />
          </div>
        )}
      </Layout2>
    </div>
  );
}

export default MenuOverview;
